import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "dg_cart_v1";

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        } catch (e) {
            return [];
        }
    });
    const [coupon, setCoupon] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const lineKey = (productId, variationId) => `${productId}-${variationId || 0}`;

    const addItem = (product, { variation = null, quantity = 1 } = {}) => {
        if (product.type === "variable" && !variation && (product.variations?.length || 0) > 0) return;
        const id = product.id;
        const vid = variation ? variation.id : 0;
        const unit = variation ? variation.price : product.price;
        const regular = variation ? variation.regular_price || variation.price : product.regular_price || product.price;
        const options = variation
            ? Object.entries(variation.attributes || {}).map(([name, value]) => ({ name, value }))
            : [];
        const maxQty = Math.max(
            1,
            Math.min((variation ? variation.stock_quantity : product.stock_quantity) ?? 10, 10)
        );
        setItems((prev) => {
            const key = lineKey(id, vid);
            const existing = prev.find((l) => l.key === key);
            if (existing) {
                return prev.map((l) =>
                    l.key === key ? { ...l, quantity: Math.min(l.quantity + quantity, maxQty) } : l
                );
            }
            return [
                ...prev,
                {
                    key,
                    product_id: id,
                    variation_id: vid,
                    quantity: Math.min(quantity, maxQty),
                    name: product.name,
                    slug: product.slug,
                    unit_price: unit,
                    regular_unit_price: regular,
                    on_sale: variation ? variation.on_sale : product.on_sale,
                    image: (variation && variation.image) || (product.images && product.images[0]?.src) || null,
                    options,
                    max_qty: maxQty,
                },
            ];
        });
    };

    const setQty = (key, qty) => {
        setItems((prev) =>
            qty <= 0
                ? prev.filter((l) => l.key !== key)
                : prev.map((l) => (l.key === key ? { ...l, quantity: Math.min(qty, l.max_qty || 10) } : l))
        );
    };

    const removeItem = (key) => setItems((prev) => prev.filter((l) => l.key !== key));
    const clearCart = () => {
        setItems([]);
        setCoupon(null);
    };

    const count = useMemo(() => items.reduce((n, l) => n + l.quantity, 0), [items]);
    const subtotal = useMemo(
        () => items.reduce((s, l) => s + Number(l.unit_price || 0) * l.quantity, 0),
        [items]
    );
    const discount = coupon ? Math.min(coupon.discount || 0, subtotal) : 0;

    const wire = {
        items,
        count,
        subtotal,
        discount,
        total: Math.max(subtotal - discount, 0),
        coupon,
        setCoupon,
        addItem,
        setQty,
        removeItem,
        clearCart,
        drawerOpen,
        setDrawerOpen,
        lineKey,
    };

    return <CartContext.Provider value={wire}>{children}</CartContext.Provider>;
}
